import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { utmVisitService } from '../api/services/UtmVisitService';
import { UTM_SOURCES, type UtmSource } from '../api/types/utm';

const MAX_PATH_LENGTH = 512;
const LISTING_ROUTE_PREFIX = '/ilan-detay';

const recordedKeys = new Set<string>();

const isUtmSource = (value: string | null): value is UtmSource =>
  value !== null && (UTM_SOURCES as readonly string[]).includes(value);

const extractListingId = (pathname: string): string | null => {
  if (!pathname.startsWith(LISTING_ROUTE_PREFIX)) {
    return null;
  }
  const segments = pathname.split('/').filter(Boolean);
  return segments.length >= 2 ? segments[1] ?? null : null;
};

const truncatePath = (value: string): string =>
  value.length > MAX_PATH_LENGTH ? value.slice(0, MAX_PATH_LENGTH) : value;

/**
 * Observes `utm_source` on every top-level route change and fires a single
 * `POST /api/UtmVisits` per unique `(source, pathname, search)` tuple. Errors
 * are swallowed silently (FR-015) and StrictMode's double-invoke is suppressed
 * via a module-scoped dedupe set.
 */
export const useUtmVisitRecorder = (): void => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawSource = params.get('utm_source');
    if (!isUtmSource(rawSource)) {
      return;
    }

    const dedupeKey = `${rawSource}|${location.pathname}|${location.search}`;
    if (recordedKeys.has(dedupeKey)) {
      return;
    }
    recordedKeys.add(dedupeKey);

    const path = truncatePath(`${location.pathname}${location.search}`);
    const listingId = extractListingId(location.pathname);

    void utmVisitService.record({
      source: rawSource,
      listingId,
      path,
    });
  }, [location.pathname, location.search]);
};
